import {
  AntragDruck,
  BankverbindungDruck,
  BeratungDruck,
  DomaenenwertDruck,
  FamilienStand,
  FomWithBankNameDruck,
  KontoinhaberAdresseDruck,
  Kuendiger,
  SparteDruckdaten,
  TelefonverkaufDruck,
  VermittlerDruck,
  VersicherungsnehmerDruck,
  VorschadenArt,
  VorversicherungDruck,
  Zahlungsart,
} from '@ps20/bff-models';
import {
  compareProductIdsIfExistingAndSpLaufnrNotExists,
  convertVermittlernummerToString,
  FremdVNVorvertrag,
  getAppEnumByAppName,
  getAppEnumBySpartenType,
  getSpartenTypeByApp,
  VnWechselGrund,
} from '@ps20-frontend/frontend-models';
import cloneDeep from 'lodash/cloneDeep';
import dayjs from 'dayjs';
import { DATEFORMAT_LOCAL_STATE, DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { selectActiveVermittlerNummer } from '@ps20-frontend/apps/checkout-store';
import { VorversicherungsItemVersicherteGefahr } from '@ps20-frontend/shop-integration-redux';
import { Vermittler } from '../../reducer/vermittler.reducer';
import { Produkt } from '../../model';
import { IAppState } from '../../localStore';
import { mapCheckoutInto } from './sparten-druckdaten.mapper';

export function mapAntraege(sparteDruckdaten: Array<SparteDruckdaten>): AntragDruck[] {
  return sparteDruckdaten.map(sparteDruckdata => ({
    antragsnummer: sparteDruckdata.antragsnummer,
    longText: sparteDruckdata.longText,
    shortText: sparteDruckdata.shortText,
    spartenType: sparteDruckdata.spartenType,
  }));
}

export function mapBankverbindung(state: IAppState): BankverbindungDruck | undefined {
  if (state.zahlung.zahlungsart === Zahlungsart.LASTSCHRIFT) {
    const kontoinhaber = state.zahlung.kontoinhaber;
    const bankverbindungDruck: BankverbindungDruck = {
      iban: state.zahlung.iban.replace(/\s/g, ''),
      kontoinhaber: {
        adelsbez: kontoinhaber?.adelsbez?.text || '',
        adelstitel: kontoinhaber?.adelstitel?.text || '',
        adresse:
          (kontoinhaber?.adresse &&
            ({
              plz: kontoinhaber.adresse.plz,
              ort: kontoinhaber.adresse.ort,
              strasse: kontoinhaber.adresse.strasse,
              hausnummer: kontoinhaber.adresse.hausnummer,
              land: kontoinhaber.adresse.land || null,
            } as KontoinhaberAdresseDruck)) ||
          null,
        akadgrad: kontoinhaber?.akadgrad?.text || '',
        anrede: kontoinhaber?.anrede,
        personenID: kontoinhaber?.kundenverwaltungID || '',
        nachname: kontoinhaber?.nachname || '',
        vorname: kontoinhaber?.vorname || '',
        firmenname: kontoinhaber?.firmenname || '',
        businessForm: kontoinhaber?.businessForm || null,
        juristisch: kontoinhaber?.juristisch || false,
        staatsangehoerigkeit: kontoinhaber?.staatsbschaft?.wert || '',
        familienstand: kontoinhaber?.juristisch
          ? FamilienStand.FAMILIE
          : kontoinhaber?.famstand?.text || '',
        gebdat: kontoinhaber?.geburtsdatum || '',
      },
    };

    if (state.zahlung.bic) {
      bankverbindungDruck.bic = state.zahlung.bic;
    }

    if (state.zahlung.bankname) {
      bankverbindungDruck.bankname = state.zahlung.bankname;
    }

    return bankverbindungDruck;
  }
}

export function mapVermittler(state: IAppState): VermittlerDruck {
  const activceVtnr = selectActiveVermittlerNummer(state.vermittler);
  return {
    vorname: state.vermittler.vorname,
    nachname: state.vermittler.nachname,
    bnrb: state.vermittler.bnrb,
    vermittlernummer: convertVermittlernummerToString(activceVtnr),
    fomWithBankNameDruck: mapFremdordnungsmerkmal(state.vermittler),
    telefonnummer: state.vermittler.telefon,
    isInkasso: state.vermittler.isInkasso,
  };
}

export function mapFremdordnungsmerkmal(vermittler: Vermittler): FomWithBankNameDruck {
  const selectedVermittlernummer = vermittler.selectedVermittlernummer;
  const vemittlerFom = vermittler.fremd
    ? selectedVermittlernummer.fremd.vermittlerFom
    : selectedVermittlernummer.eigen.vermittlerFom;
  const fomWithBankNameDruck: FomWithBankNameDruck = {
    vermittlerFom: vemittlerFom,
  };
  if (vermittler.fom) {
    fomWithBankNameDruck.fremdordnungsmerkmal = vermittler.fom;
  }
  return fomWithBankNameDruck;
}

export function mapVersicherungsnehmer(state: IAppState): VersicherungsnehmerDruck {
  const geburtsdatum = toISODate(state.versicherungsnehmer.geburtsdatum);
  const versicherungsnehmer = state.versicherungsnehmer;
  return {
    adresse: {
      hausnrzus: versicherungsnehmer.hausnrzus,
      hausnummer: versicherungsnehmer.hausnummer,
      land: versicherungsnehmer.land as DomaenenwertDruck,
      ort: versicherungsnehmer.ort,
      ortsteil: versicherungsnehmer.ortsteil,
      plz: versicherungsnehmer.plz,
      strasse: versicherungsnehmer.strasse,
      adresszusatz: versicherungsnehmer.adresszusatz,
      adresszusatzkennzeichen:
        versicherungsnehmer.adresszusatzkennzeichen &&
        versicherungsnehmer.adresszusatzkennzeichen.text,
    },
    anrede: versicherungsnehmer.anrede,
    email: versicherungsnehmer.email.wert,
    telefon: valueOrDefault(versicherungsnehmer.telefon && versicherungsnehmer.telefon.wert, ''),
    fax: valueOrDefault(versicherungsnehmer.fax && versicherungsnehmer.fax.wert, ''),
    mobil: valueOrDefault(versicherungsnehmer.mobil && versicherungsnehmer.mobil.wert, ''),
    geburtsdatum,
    nachname: versicherungsnehmer.nachname,
    vorname: versicherungsnehmer.vorname,
    firmenname: versicherungsnehmer.firmenname,
    juristisch: versicherungsnehmer.juristisch,
    businessForm: versicherungsnehmer.businessForm,
    adelstitel: valueOrDefault(
      versicherungsnehmer.adelstitel && versicherungsnehmer.adelstitel.text,
      '',
    ),
    akadgrad: valueOrDefault(versicherungsnehmer.akadgrad && versicherungsnehmer.akadgrad.text, ''),
    adelsbez: valueOrDefault(versicherungsnehmer.adelsbez && versicherungsnehmer.adelsbez.text, ''),
    namenszusatz: valueOrDefault(
      versicherungsnehmer.namenszusatz && versicherungsnehmer.namenszusatz.text,
      '',
    ),
    famstand: valueOrDefault(versicherungsnehmer.famstand && versicherungsnehmer.famstand.text, ''),
    staatsbschaft: valueOrDefault(
      versicherungsnehmer.staatsbschaft && versicherungsnehmer.staatsbschaft.wert,
      '',
    ),
    personenID: valueOrDefault(versicherungsnehmer.kundenverwaltungID, ''),
  };
}

export function mapTelefonverkauf(state: IAppState): TelefonverkaufDruck {
  return {
    fandMitVNStatt: state.telefonverkauf.fandMitVNStatt,
    vornameGespraechspartner: state.telefonverkauf.vornameGespraechspartner,
    nachnameGespraechspartner: state.telefonverkauf.nachnameGespraechspartner,
    datumGespraech: state.telefonverkauf.datumGespraech,
    uhrzeitGespraech: state.telefonverkauf.uhrzeitGespraech,
    versandArtAVB: state.telefonverkauf.versandArtAVB,
    emailAVBVersand: state.telefonverkauf.emailAVBVersand,
  };
}

export function mapBeratungDruck(state: IAppState): BeratungDruck {
  return {
    besonderheiten: state.beratung.besonderheiten,
    besonderheitenbesprochen: isBesonderheitenBesprochen(state),
    beratername: getBeratername(state),
    beratungsdatum: toISODate(state.beratung.beratungsdatum),
  };
}

export function isBesonderheitenBesprochen(state: IAppState): boolean {
  return (
    state.beratung.besonderheitenBesprochen ||
    state.beratung.extremwetterschutzBesonderheiten.length > 0
  );
}

function getBeratername(state: IAppState): string {
  return state.beratung.beratername ?? `${state.vermittler.vorname} ${state.vermittler.nachname}`;
}

export function toISODate(date: string): string {
  return date.split('.').reverse().join('-');
}

function mapVersicherteGefahren(
  versicherteGefahren: Array<VorversicherungsItemVersicherteGefahr>,
): VorschadenArt[] {
  return versicherteGefahren
    .filter(versicherteGefahr => versicherteGefahr.selected)
    .map(versicherteGefahr => versicherteGefahr.value);
}

export function addCheckoutDataToSparteDruckdaten(
  state: IAppState,
  sparteDruckdaten: SparteDruckdaten,
): SparteDruckdaten {
  let spartenDruckDatenClone: SparteDruckdaten = cloneDeep(sparteDruckdaten);

  const produktForDruckdaten: Produkt = state.produkte.find(
    produkt =>
      getSpartenTypeByApp(produkt.appId) === sparteDruckdaten.spartenType &&
      produkt.angebotsId === sparteDruckdaten.angebotsId,
  );

  spartenDruckDatenClone.beginn = toISODate(produktForDruckdaten.beginn);
  spartenDruckDatenClone.ablauf = toISODate(produktForDruckdaten.ablauf);
  spartenDruckDatenClone.vertragsdauer = produktForDruckdaten.laufzeit.toString();
  spartenDruckDatenClone.abweichungVomZielmarkt = produktForDruckdaten.abweichungVomZielmarkt;
  spartenDruckDatenClone.zielmarktBegruendung = produktForDruckdaten.zielmarktBegruendung;
  if (produktForDruckdaten.kooperation) {
    spartenDruckDatenClone.kooperation = produktForDruckdaten.kooperation;
  }

  if (sparteDruckdaten.vorvertragsnummern && sparteDruckdaten.vorvertragsnummern.length > 0) {
    addVnWechselGrundToVorvertragsnummernDruck(spartenDruckDatenClone, state);
  }

  const vorversicherung = state.vorversicherungen.find(
    elem =>
      elem.appId === getAppEnumByAppName(produktForDruckdaten.appId) &&
      elem.angebotsId === sparteDruckdaten.angebotsId,
  );

  if (vorversicherung?.vorhanden) {
    spartenDruckDatenClone.vorversicherungenDruck = vorversicherung.vorversicherungsListe.map(
      vorversicherungDruck => {
        const newAblaufdatum = vorversicherungDruck.ablaufdatum
          ? dayjs(vorversicherungDruck.ablaufdatum, DATEFORMAT_LOCAL_STATE)
          : null;

        const vorversicherungForDruck: VorversicherungDruck = {
          gesellschaft: valueOrDefault(vorversicherungDruck?.versicherungsgesellschaft, ''),
          vsnr: vorversicherungDruck.vsnr,
          versicherteGefahren: mapVersicherteGefahren(vorversicherungDruck.versicherteGefahren),
          ablaufdatum: newAblaufdatum?.isValid()
            ? newAblaufdatum.format(DATEFORMAT_SHORT_ISO)
            : null,
        };

        if (vorversicherungDruck.vomVorversichererGekuendigt !== undefined) {
          vorversicherungForDruck.kuendiger = vorversicherungDruck.vomVorversichererGekuendigt
            ? Kuendiger.VERSICHERER
            : Kuendiger.VERSICHERUNGSNEHMER;
        }

        return vorversicherungForDruck;
      },
    );
  }
  spartenDruckDatenClone = mapCheckoutInto(state, spartenDruckDatenClone);
  return spartenDruckDatenClone;
}

function valueOrDefault<T>(value: T, defaultValue: T): T {
  return value || defaultValue;
}

function addVnWechselGrundToVorvertragsnummernDruck(
  sparteDruckdaten: SparteDruckdaten,
  state: IAppState,
): void {
  if (state.vnWechselAngaben?.vnWechselGrundAngabenVorhanden) {
    const fremdVNVorvertraege: Array<FremdVNVorvertrag> =
      state.vnWechselAngaben.angabenListe.reduce(
        (acc, angabe) => acc.concat(angabe.fremdVNVorvertraege),
        [],
      );

    sparteDruckdaten.vorvertragsnummern.forEach(vorvertragsnummerToUpdate => {
      const wechselGrund: VnWechselGrund = fremdVNVorvertraege
        .filter(
          vorvertrag =>
            vorvertrag.vorvertragsnummer.vertragsnummer ===
              vorvertragsnummerToUpdate.vorvertragsnummer &&
            compareProductIdsIfExistingAndSpLaufnrNotExists(
              vorvertrag.vorvertragsnummer.produktIds,
              vorvertragsnummerToUpdate.produktIds,
              vorvertrag.vorvertragsnummer.spLaufnummer,
              vorvertragsnummerToUpdate.spLaufnummer,
            ),
        )
        .filter(vorvertrag => vorvertrag.vnWechselGrund && vorvertrag.vnWechselGrund.length > 0)
        .reduce((acc, vorvertrag) => acc.concat(vorvertrag.vnWechselGrund), [])
        .find(grund => grund.appId === getAppEnumBySpartenType(sparteDruckdaten.spartenType));

      if (wechselGrund) {
        vorvertragsnummerToUpdate.wechselGrund = wechselGrund.value;
      }
    });
  }
}
